import { Box } from "@mui/material";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";

// solid: filled. soft: tinted bg + 1px border. soft icon falls back to text color.
const TONES = {
  neutral: {
    solid: { bg: colorPalette.mercury[950], text: colors.white },
    soft: {
      bg: colorPalette.mercuryAlpha[8],
      border: colorPalette.mercuryAlpha[18],
      text: colorPalette.mercuryAlpha[65],
    },
  },
  info: {
    solid: { bg: colorPalette.cerulean[700], text: colors.white },
    soft: {
      bg: colorPalette.cerulean[100],
      border: colorPalette.cerulean[700],
      text: colorPalette.cerulean[700],
      icon: colorPalette.cerulean[1000],
    },
  },
  success: {
    solid: { bg: colorPalette.salem[700], text: colors.white },
    soft: {
      bg: colorPalette.salem[100],
      border: colorPalette.salem[700],
      text: colorPalette.salem[700],
      icon: colorPalette.salem[1000],
    },
  },
  warning: {
    solid: { bg: colorPalette.yellow[700], text: colors.white },
    soft: {
      bg: colorPalette.yellow[100],
      border: colorPalette.yellow[700],
      text: colorPalette.yellow[700],
      icon: colorPalette.yellow[1000],
    },
  },
  danger: {
    solid: { bg: colorPalette.red[700], text: colors.white },
    soft: {
      bg: colorPalette.red[100],
      border: colorPalette.red[700],
      text: colorPalette.red[700],
      icon: colorPalette.red[1000],
    },
  },
  brand: {
    solid: { bg: colorPalette.brand[700], text: colors.white },
    soft: {
      bg: colorPalette.brand[100],
      border: colorPalette.brand[800],
      text: colorPalette.brand[700],
    },
  },
  promo: {
    solid: { bg: colorPalette.accentPurple.solid, text: colors.white },
    soft: {
      bg: colorPalette.accentPurple.bg,
      border: colorPalette.accentPurple.solid,
      text: colorPalette.accentPurple.content,
    },
  },
};

/**
 * Badge
 *
 * Props:
 * - tone: neutral | info | success | warning | danger | brand | promo
 * - emphasis: solid | soft
 * - icon: React node (e.g. an @mui/icons-material icon), sized by the badge
 * - bgColor / textColor / borderColor: override the tone's colors
 * - sx: extra styles, merged last
 */
const Badge = ({
  tone = "neutral",
  emphasis = "solid",
  icon,
  bgColor,
  textColor,
  borderColor,
  sx: sxOverride = {},
  children,
  ...rest
}) => {
  const style = (TONES[tone] || TONES.neutral)[emphasis] || TONES.neutral.solid;
  const border = borderColor || style.border;

  return (
    <Box
      component="span"
      {...rest}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        // Figma strokes sit inside the 20px height, so trim 1px to offset the CSS border
        padding: border ? "3px 7px" : "4px 8px",
        borderRadius: "4px",
        boxSizing: "border-box",
        whiteSpace: "nowrap",
        fontFamily: '"Lexend", sans-serif',
        fontSize: 12,
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "100%",
        letterSpacing: "0.15px",
        backgroundColor: bgColor || style.bg,
        color: textColor || style.text,
        border: border ? `1px solid ${border}` : "none",
        ...sxOverride,
      }}
    >
      {icon && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: "inline-flex",
            // MUI icons have inner padding, so 12px reads like Figma's 10px glyph
            fontSize: 12,
            color: textColor || style.icon || "inherit",
            "& svg": { fontSize: "inherit", width: "1em", height: "1em" },
          }}
        >
          {icon}
        </Box>
      )}
      {children}
    </Box>
  );
};

export default Badge;
